import { MutableRefObject, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { parseProfileSidebarParams } from 'utils/profileSidebarParams';
import { isAgentChatOpen } from './agentChatParam';

type Params = {
  /** The assistant feature gate — no restores while it's off. */
  enabled: boolean;
  /**
   * Called when a navigation dropped the panel's param. The caller re-applies
   * it (openAgentChat) and marks the reopen as a restore rather than a fresh
   * user open (see the suppress ref in AgentChatSidebar).
   */
  onRestore: () => void;
  /**
   * Set true by the caller when it removes the param as an EXPLICIT close that
   * may arrive here batched with a navigation: an MFE sider's open-click first
   * asks the assistant to yield and then navigates (schedule's announcements /
   * day-sidebar buttons). React commits the yield's param removal and the
   * navigation's pathname change together, so this hook would otherwise see
   * only "pathname changed and the open panel's param vanished" —
   * indistinguishable from an app transition — and restore the panel the yield
   * just closed. Consumed (reset to false) at the next evaluation, so it
   * suppresses exactly one drop.
   */
  explicitCloseRef?: MutableRefObject<boolean>;
};

/**
 * Keeps the assistant panel open across app transitions.
 *
 * The `agent` query param is the panel's canonical state, but nav links and
 * MFE-built URLs assemble their destination from scratch, so navigating to
 * another app would drop the param and silently close the panel. Treat the
 * param as shell-owned instead: when the previous location had the panel open
 * and a navigation that CHANGED the pathname lost the param, the caller
 * re-applies it in place (a replace — the navigation already made the history
 * entry).
 *
 * Explicit closes are unaffected: closeAgentChat and the yield paths (the
 * composer or an MFE sider taking the slot) rewrite the URL on the SAME
 * pathname, so no restore fires — and a yield whose click navigates in the
 * same commit is marked via `explicitCloseRef` (see the param doc) so the
 * batched pathname change doesn't read as a transition. The trade-off is that
 * a same-pathname URL rewrite that rebuilds the query from scratch would read
 * as a close — but cross-app writers go through `replaceSharedUrl`-style
 * rebuilds that preserve foreign params, so in practice only real closes look
 * like closes.
 *
 * Returns whether a restore is PENDING for the location being rendered: the
 * param was just dropped by such a navigation and the effect below is about to
 * re-apply it. The caller renders the panel as still-open during that window
 * so the restore never paints as a close-then-reopen flicker; the param (and
 * this returning false again) catches up one effect pass later.
 */
export const useRestoreAgentChatOnNavigation = ({
  enabled,
  onRestore,
  explicitCloseRef,
}: Params): boolean => {
  const { pathname, search } = useLocation();
  const isOpen = isAgentChatOpen(search);

  // Latest-callback ref so the effect below doesn't re-run (and mis-track the
  // previous location) when the caller's callback identity changes.
  const onRestoreRef = useRef(onRestore);
  useEffect(() => {
    onRestoreRef.current = onRestore;
  });

  // A navigation that carries an open people profile is a newer claim on the
  // right-hand slot (a profile deep link) — restoring would strip it, since
  // opening the assistant closes the profile (last-opened-wins). Yield to it.
  const profileOpen = parseProfileSidebarParams(search).userId !== null;

  // The previous COMMITTED location: written only in the effect below, so
  // discarded/duplicated renders all read the same value and the render-time
  // `restorePending` derivation stays consistent until the commit.
  const prevRef = useRef({ pathname, wasOpen: isOpen });

  // Whether the URL change being evaluated came from a REAL history traversal
  // (browser Back/Forward). Real traversals dispatch TRUSTED popstate events;
  // every programmatic cross-app write dispatches a synthetic (untrusted) one
  // (replaceSharedUrl, single-spa's patched history methods), so the latest
  // event's trustedness classifies the change. A history entry's URL already
  // records whether the panel was open when the user was there — opens and
  // restores write the param in place — so on a traversal the entry is
  // authoritative: re-applying the param would both defeat the state the user
  // navigated back to and race any MFE sider mounted at that entry (its
  // arbitration reads the restored param as a fresh open and closes itself —
  // a self-destructing Back). Consumed each evaluation, like the explicit
  // close mark, so a stale value can't suppress a later app transition.
  const historyTraversalRef = useRef(false);
  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      historyTraversalRef.current = event.isTrusted;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const computeRestorePending = (prev: typeof prevRef.current): boolean =>
    enabled &&
    prev.wasOpen &&
    !isOpen &&
    !profileOpen &&
    pathname !== prev.pathname;

  const restorePending =
    !explicitCloseRef?.current &&
    !historyTraversalRef.current &&
    computeRestorePending(prevRef.current);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { pathname, wasOpen: isOpen };
    // Consume the one-shot marks: each covers exactly the change this effect
    // pass is evaluating (both are set synchronously before its commit).
    const explicitClose = explicitCloseRef?.current ?? false;
    if (explicitCloseRef) {
      explicitCloseRef.current = false;
    }
    const historyTraversal = historyTraversalRef.current;
    historyTraversalRef.current = false;
    if (!explicitClose && !historyTraversal && computeRestorePending(prev)) {
      onRestoreRef.current();
    }
    // computeRestorePending reads only values listed here (via render scope);
    // the refs are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pathname, isOpen, profileOpen]);

  return restorePending;
};
